import { ActiveBadge, CardRow } from "@/components/card-row";
import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { CopyId } from "@/components/copy-id";
import {
  type ProfileCard,
  type UpdateCardPayload,
  cardsProvider,
} from "@/providers/cards.provider";
import {
  IconCards,
  IconChevronLeft,
  IconChevronRight,
  IconCheck,
  IconLoader2,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
  IconUserMinus,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import { formatImageInfo, useImageInfo } from "@/hooks/use-image-info";
import {
  ImageCompressionSlider,
  compressImageFile,
} from "@/components/image-compression-slider";
import { usersProvider, type User } from "@/providers/users.provider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE = 50;


// ── User search picker ────────────────────────────────────────────────────────

function UserSearchPicker({
  selectedUserId,
  onSelect,
  resetKey,
}: {
  selectedUserId: string;
  onSelect: (user: User) => void;
  resetKey?: string;
}) {
  const [input, setInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    setInput("");
    setDebouncedQ("");
  }, [resetKey]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(input.trim()), 300);
    return () => clearTimeout(id);
  }, [input]);

  const { data: users, isLoading } = useQuery({
    queryKey: ["grant-user-search", debouncedQ],
    queryFn: () => usersProvider.search(debouncedQ),
    enabled: debouncedQ.length >= 1,
    staleTime: 30_000,
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Search by username, email or ID…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="pl-8 pr-8 text-xs"
        />
        {input && (
          <button
            type="button"
            onClick={() => { setInput(""); setDebouncedQ(""); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <IconX className="size-3.5" />
          </button>
        )}
      </div>
      {isLoading && debouncedQ && (
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      )}
      {users && users.length > 0 && (
        <div className="flex flex-col divide-y rounded-md border text-xs max-h-44 overflow-y-auto">
          {users.map((u) => (
            <button
              key={u.user_id}
              type="button"
              onClick={() => onSelect(u)}
              className={`flex items-center gap-2 px-2 py-1.5 text-left hover:bg-muted/60 transition-colors ${selectedUserId === u.user_id ? "bg-muted" : ""}`}
            >
              {u.avatar_url ? (
                <img src={u.avatar_url} alt={u.username} className="size-5 rounded-full object-cover shrink-0" />
              ) : (
                <div className="size-5 rounded-full bg-muted border shrink-0" />
              )}
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-medium truncate">{u.username}</span>
                <span className="text-muted-foreground font-mono truncate">{u.user_id}</span>
              </div>
              {selectedUserId === u.user_id && <IconCheck className="size-3.5 ml-auto shrink-0 text-green-600" />}
            </button>
          ))}
        </div>
      )}
      {debouncedQ && !isLoading && users?.length === 0 && (
        <p className="text-xs text-muted-foreground">No users found.</p>
      )}
    </div>
  );
}

// ── Create card dialog ────────────────────────────────────────────────────────

function CreateCardSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#000000");
  const [secondaryColor, setSecondaryColor] = useState("#ffffff");
  const [font, setFont] = useState("Inter");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [createQuality, setCreateQuality] = useState(85);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const compressed = await compressImageFile(imageFile!, createQuality);
      const result = await cardsProvider.create({
        name: name.trim(),
        description: description.trim() || undefined,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        font: font.trim() || "Inter",
      });
      await cardsProvider.uploadImageToStorage(result, compressed);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-cards"] });
      toast.success("Card created");
      resetAndClose();
    },
    onError: (err) => toast.error(err.message),
  });

  function resetAndClose() {
    setName("");
    setDescription("");
    setPrimaryColor("#000000");
    setSecondaryColor("#ffffff");
    setFont("Inter");
    setImageFile(null);
    setCreateQuality(85);
    onClose();
  }

  function handleClose() {
    if (!isPending) resetAndClose();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    setImageFile(file);
    e.target.value = "";
  }

  const formattedSize = imageFile 
    ? imageFile.size >= 1024 * 1024 
      ? `${(imageFile.size / (1024 * 1024)).toFixed(2)} MB` 
      : `${(imageFile.size / 1024).toFixed(1)} KB`
    : null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>New Profile Card</SheetTitle>
          <SheetDescription>Create a new collectible card for users.</SheetDescription>
        </SheetHeader>
        <div className="px-4 flex flex-col gap-4">
          {/* Image picker */}
          <div className="flex flex-col gap-1.5">
            <Label>Image</Label>
            <div className="flex items-center gap-3">
              <div className="size-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => fileRef.current?.click()}>
                {imageFile ? (
                  <img
                    src={URL.createObjectURL(imageFile)}
                    alt="preview"
                    className="size-full object-cover"
                  />
                ) : (
                  <IconPhoto className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  className="gap-1.5"
                >
                  <IconUpload className="size-3.5" />
                  {imageFile ? "Change image" : "Choose image"}
                </Button>
                {imageFile ? (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-destructive text-left"
                    onClick={() => setImageFile(null)}
                  >
                    Remove
                  </button>
                ) : null}
                {formattedSize && (
                  <p className="text-xs text-muted-foreground">{formattedSize}</p>
                )}
                <p className="text-xs text-muted-foreground">Recommended: 1215 × 766 px</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>

          {imageFile && (
            <ImageCompressionSlider
              file={imageFile}
              quality={createQuality}
              onChange={setCreateQuality}
            />
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-card-name">Name</Label>
            <Input
              id="new-card-name"
              placeholder="e.g. Founder"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-card-desc">Description</Label>
            <Textarea
              id="new-card-desc"
              placeholder="Short description…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex gap-4">
            <div className="flex flex-col gap-1.5 flex-1">
              <Label htmlFor="new-card-primary">Primary color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="size-8 rounded border cursor-pointer p-0.5 bg-transparent"
                />
                <Input
                  id="new-card-primary"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="font-mono text-xs"
                  maxLength={7}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5 flex-1">
              <Label htmlFor="new-card-secondary">Secondary color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="size-8 rounded border cursor-pointer p-0.5 bg-transparent"
                />
                <Input
                  id="new-card-secondary"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="font-mono text-xs"
                  maxLength={7}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-card-font">Font</Label>
            <Input
              id="new-card-font"
              value={font}
              onChange={(e) => setFont(e.target.value)}
              placeholder="e.g. Inter"
            />
          </div>
        </div>
        <SheetFooter className="px-4">
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => mutate()}
            disabled={isPending || !name.trim() || !imageFile}
          >
            {isPending ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconPlus className="size-4" />
            )}
            Create
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ── Card detail sheet ─────────────────────────────────────────────────────────

function CardDetailSheet({
  card,
  onClose,
}: {
  card: ProfileCard | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editActive, setEditActive] = useState(false);
  const [editPrimaryColor, setEditPrimaryColor] = useState("#000000");
  const [editSecondaryColor, setEditSecondaryColor] = useState("#ffffff");
  const [editFont, setEditFont] = useState("");
  const [editEmbedContent, setEditEmbedContent] = useState(false);
  const [selectedGrantUser, setSelectedGrantUser] = useState<User | null>(null);
  const [userLookupInput, setUserLookupInput] = useState("");
  const [userLookupId, setUserLookupId] = useState("");
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [uploadQuality, setUploadQuality] = useState(85);

  // Sync edit state when card changes
  const prevCardId = useRef<string | null>(null);
  if (card && card.profile_card_id !== prevCardId.current) {
    prevCardId.current = card.profile_card_id;
    setEditName(card.name);
    setEditDesc(card.description ?? "");
    setEditActive(card.is_active);
    setEditPrimaryColor(card.primary_color);
    setEditSecondaryColor(card.secondary_color);
    setEditFont(card.font ?? "");
    setEditEmbedContent(card.embed_content);
    setSelectedGrantUser(null);
    setPendingUploadFile(null);
    setUploadQuality(85);
    setUserLookupInput("");
    setUserLookupId("");
  }

  const updateMut = useMutation({
    mutationFn: (payload: UpdateCardPayload) =>
      cardsProvider.update(card!.profile_card_id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-cards"] });
      toast.success("Card updated");
    },
    onError: (err) => toast.error(err.message),
  });

  const uploadMut = useMutation({
    mutationFn: async () => {
      const compressed = await compressImageFile(pendingUploadFile!, uploadQuality);
      const uploadUrl = await cardsProvider.getImageUploadUrl(card!.profile_card_id);
      await cardsProvider.uploadImageToStorage(uploadUrl, compressed);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-cards"] });
      toast.success("Image uploaded");
      setPendingUploadFile(null);
      setUploadQuality(85);
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => cardsProvider.delete(card!.profile_card_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile-cards"] });
      toast.success("Card deleted");
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const grantMut = useMutation({
    mutationFn: (userId: string) =>
      cardsProvider.grantToUser(card!.profile_card_id, userId),
    onSuccess: (_, userId) => {
      qc.invalidateQueries({ queryKey: ["user-cards-lookup", userId] });
      qc.invalidateQueries({ queryKey: ["user-cards", userId] });
      toast.success("Card granted");
    },
    onError: (err) => toast.error(err.message),
  });

  const revokeMut = useMutation({
    mutationFn: (userId: string) =>
      cardsProvider.revokeFromUser(card!.profile_card_id, userId),
    onSuccess: (_, userId) => {
      qc.invalidateQueries({ queryKey: ["user-cards-lookup", userId] });
      qc.invalidateQueries({ queryKey: ["user-cards", userId] });
      toast.success("Card revoked");
      setSelectedGrantUser(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const setActiveMut = useMutation({
    mutationFn: (userId: string) =>
      cardsProvider.setActiveCard(userId, card!.profile_card_id),
    onSuccess: () => toast.success("Active card set"),
    onError: (err) => toast.error(err.message),
  });

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    setPendingUploadFile(file);
    setUploadQuality(85);
    e.target.value = "";
  }

  const {
    data: userCards,
    isLoading: userCardsLoading,
    isError: userCardsError,
  } = useQuery({
    queryKey: ["user-cards-lookup", userLookupId],
    queryFn: () => cardsProvider.getCardsForUser(userLookupId),
    enabled: !!userLookupId,
    staleTime: 30_000,
  });

  const ownsThisCard =
    userCards?.some((c) => c.profile_card_id === card?.profile_card_id) ?? false;

  const imgSrc = card?.img_url ?? null;
  const cardImageInfo = useImageInfo(imgSrc);
  const anyPending =
    updateMut.isPending || deleteMut.isPending || uploadMut.isPending;

  const isDirty =
    card &&
    (editName !== card.name ||
      editDesc !== (card.description ?? "") ||
      editActive !== card.is_active ||
      editPrimaryColor !== card.primary_color ||
      editSecondaryColor !== card.secondary_color ||
      editFont !== (card.font ?? "") ||
      editEmbedContent !== card.embed_content);

  return (
    <Sheet
      open={!!card}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {card && (
          <>
            <SheetHeader>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-4 pr-6">
                  <SheetTitle className="truncate">{card.name}</SheetTitle>
                  <CopyId id={card.profile_card_id} />
                </div>
                {cardImageInfo && (
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="secondary" className="font-mono text-[10px] tabular-nums">
                      {cardImageInfo.width} × {cardImageInfo.height}px
                    </Badge>
                    {cardImageInfo.size && (
                      <Badge variant="outline" className="font-mono text-[10px] tabular-nums">
                        {cardImageInfo.size}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </SheetHeader>

            {/* Image */}
            <div className="px-4 flex flex-col gap-2 mt-4">
              {imgSrc ? (
                <div
                  className="w-full rounded-lg border bg-muted overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => fileRef.current?.click()}
                >
                  <img
                    src={imgSrc}
                    alt={card.name}
                    className="w-full h-auto"
                  />
                </div>
              ) : (
                <div
                  className="w-full rounded-lg border bg-muted flex items-center justify-center py-10 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => fileRef.current?.click()}
                >
                  <IconPhoto className="size-10 text-muted-foreground" />
                </div>
              )}
              {cardImageInfo && (
                <p className="text-[10px] text-muted-foreground tabular-nums text-center uppercase tracking-wider">
                  {formatImageInfo(cardImageInfo)}
                </p>
              )}
              <div className="flex flex-col gap-2">
                {pendingUploadFile ? (
                  <>
                    <ImageCompressionSlider
                      file={pendingUploadFile}
                      quality={uploadQuality}
                      onChange={setUploadQuality}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => uploadMut.mutate()}
                        disabled={uploadMut.isPending}
                        className="gap-1.5"
                      >
                        {uploadMut.isPending ? (
                          <IconLoader2 className="size-3.5 animate-spin" />
                        ) : (
                          <IconUpload className="size-3.5" />
                        )}
                        Upload
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={uploadMut.isPending}
                        onClick={() => setPendingUploadFile(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploadMut.isPending}
                      className="gap-1.5"
                    >
                      <IconUpload className="size-3.5" />
                      {imgSrc ? "Replace image" : "Upload image"}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Recommended: 1215 × 766 px
                    </p>
                  </>
                )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
              </div>
            </div>

            <Separator />

            {/* Edit metadata */}
            <div className="px-4 flex flex-col gap-4">
              <h3 className="font-semibold text-sm">Details</h3>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-card-name">Name</Label>
                <Input
                  id="edit-card-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-card-desc">Description</Label>
                <Textarea
                  id="edit-card-desc"
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-card-active">Active</Label>
                <Switch
                  id="edit-card-active"
                  checked={editActive}
                  onCheckedChange={setEditActive}
                />
              </div>
              <div className="flex gap-4">
                <div className="flex flex-col gap-1.5 flex-1">
                  <Label htmlFor="edit-card-primary">Primary color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={editPrimaryColor}
                      onChange={(e) => setEditPrimaryColor(e.target.value)}
                      className="size-8 rounded border cursor-pointer p-0.5 bg-transparent"
                    />
                    <Input
                      id="edit-card-primary"
                      value={editPrimaryColor}
                      onChange={(e) => setEditPrimaryColor(e.target.value)}
                      className="font-mono text-xs"
                      maxLength={7}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <Label htmlFor="edit-card-secondary">Secondary color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={editSecondaryColor}
                      onChange={(e) => setEditSecondaryColor(e.target.value)}
                      className="size-8 rounded border cursor-pointer p-0.5 bg-transparent"
                    />
                    <Input
                      id="edit-card-secondary"
                      value={editSecondaryColor}
                      onChange={(e) => setEditSecondaryColor(e.target.value)}
                      className="font-mono text-xs"
                      maxLength={7}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-card-font">Font</Label>
                <Input
                  id="edit-card-font"
                  value={editFont}
                  onChange={(e) => setEditFont(e.target.value)}
                  placeholder="e.g. Inter"
                  className="text-sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-card-embed-content">Embed content</Label>
                <Switch
                  id="edit-card-embed-content"
                  checked={editEmbedContent}
                  onCheckedChange={setEditEmbedContent}
                />
              </div>
              {isDirty && (
                <Button
                  size="sm"
                  onClick={() =>
                    updateMut.mutate({
                      name: editName.trim(),
                      description: editDesc.trim(),
                      is_active: editActive,
                      primary_color: editPrimaryColor,
                      secondary_color: editSecondaryColor,
                      font: editFont.trim(),
                      embed_content: editEmbedContent,
                    })
                  }
                  disabled={updateMut.isPending || !editName.trim()}
                  className="self-end"
                >
                  {updateMut.isPending ? (
                    <IconLoader2 className="size-4 animate-spin" />
                  ) : null}
                  Save changes
                </Button>
              )}
            </div>

            <Separator />

            {/* User lookup */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm">Look up user cards</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="User ID"
                  value={userLookupInput}
                  onChange={(e) => setUserLookupInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && setUserLookupId(userLookupInput.trim())}
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!userLookupInput.trim()}
                  onClick={() => setUserLookupId(userLookupInput.trim())}
                  className="shrink-0"
                >
                  <IconSearch className="size-3.5" />
                </Button>
              </div>

              {userLookupId && (
                <div className="flex flex-col gap-2">
                  {userCardsLoading ? (
                    <div className="flex flex-col gap-1.5">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  ) : userCardsError ? (
                    <p className="text-xs text-muted-foreground">Failed to load cards for this user.</p>
                  ) : userCards?.length === 0 ? (
                    <p className="text-xs text-muted-foreground">This user has no cards.</p>
                  ) : (
                    <>
                      <div className={`flex items-center gap-1.5 text-xs font-medium ${ownsThisCard ? "text-green-600" : "text-muted-foreground"}`}>
                        {ownsThisCard
                          ? <><IconCheck className="size-3.5" /> Owns this card</>
                          : <><IconX className="size-3.5" /> Does not own this card</>
                        }
                      </div>
                      <div className="flex flex-col divide-y rounded-md border text-xs">
                        {userCards?.map((c) => (
                          <div
                            key={c.profile_card_id}
                            className={`flex items-center gap-2 px-2 py-1.5 ${c.profile_card_id === card?.profile_card_id ? "bg-muted/60" : ""}`}
                          >
                            {c.img_url ? (
                              <img src={c.img_url} alt={c.name} className="size-5 rounded object-cover shrink-0" />
                            ) : (
                              <div className="size-5 rounded bg-muted border shrink-0" />
                            )}
                            <span className={c.profile_card_id === card?.profile_card_id ? "font-medium" : ""}>{c.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <Separator />

            {/* Grant / Revoke */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm">Grant / Revoke</h3>
              <UserSearchPicker
                selectedUserId={selectedGrantUser?.user_id ?? ""}
                onSelect={setSelectedGrantUser}
                resetKey={card.profile_card_id}
              />
              {selectedGrantUser && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs bg-muted/40">
                    {selectedGrantUser.avatar_url ? (
                      <img src={selectedGrantUser.avatar_url} alt={selectedGrantUser.username} className="size-5 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="size-5 rounded-full bg-muted border shrink-0" />
                    )}
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="font-medium truncate">{selectedGrantUser.username}</span>
                      <span className="text-muted-foreground font-mono truncate">{selectedGrantUser.user_id}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedGrantUser(null)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                      <IconX className="size-3.5" />
                    </button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={grantMut.isPending}
                      onClick={() => grantMut.mutate(selectedGrantUser.user_id)}
                      className="gap-1.5"
                    >
                      {grantMut.isPending ? (
                        <IconLoader2 className="size-3.5 animate-spin" />
                      ) : (
                        <IconUserPlus className="size-3.5" />
                      )}
                      Grant
                    </Button>
                    <ConfirmDestructiveDialog
                      confirmValue={selectedGrantUser.user_id}
                      confirmLabel="user ID"
                      title={`Revoke this card from @${selectedGrantUser.username}?`}
                      description="This removes the card from this user. This cannot be undone."
                      actionLabel="Revoke"
                      onConfirm={() => revokeMut.mutate(selectedGrantUser.user_id)}
                      isPending={revokeMut.isPending}
                      skipFeatureFlag
                      trigger={({ disabled }) => (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={disabled}
                          className="gap-1.5"
                        >
                          {revokeMut.isPending ? (
                            <IconLoader2 className="size-3.5 animate-spin" />
                          ) : (
                            <IconUserMinus className="size-3.5" />
                          )}
                          Revoke
                        </Button>
                      )}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={setActiveMut.isPending}
                      onClick={() => setActiveMut.mutate(selectedGrantUser.user_id)}
                      className="gap-1.5"
                    >
                      {setActiveMut.isPending ? (
                        <IconLoader2 className="size-3.5 animate-spin" />
                      ) : (
                        <IconCheck className="size-3.5" />
                      )}
                      Make active
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Delete */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm text-destructive">
                Danger zone
              </h3>
              <ConfirmDestructiveDialog
                confirmValue={card.profile_card_id}
                confirmLabel="card ID"
                title="Delete this card?"
                description="This will delete the card and remove it from all users. This cannot be undone."
                actionLabel="Delete card"
                onConfirm={() => deleteMut.mutate()}
                isPending={deleteMut.isPending}
                trigger={({ disabled }) => (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/30 hover:bg-destructive/10 self-start gap-1.5"
                    disabled={disabled || anyPending}
                  >
                    <IconTrash className="size-3.5" />
                    Delete card
                  </Button>
                )}
              />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function UserCardsPage() {
  const [page, setPage] = useState(1);
  const [selectedCard, setSelectedCard] = useState<ProfileCard | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: cards, isLoading, isError } = useQuery({
    queryKey: ["profile-cards"],
    queryFn: () => cardsProvider.listAll(),
    staleTime: 60_000,
  });

  const paginated = cards?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) ?? [];
  const hasMore = (cards?.length ?? 0) > page * PAGE_SIZE;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Profile Cards" />

      <div className="flex flex-col gap-4 flex-1 p-4 lg:p-6">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <IconCards className="size-4" />
            <span className="text-sm">
              {isLoading
                ? "Loading…"
                : cards
                  ? `${cards.length} card${cards.length !== 1 ? "s" : ""}`
                  : ""}
            </span>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
            <IconPlus className="size-4" />
            New card
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Card ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Colors</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Failed to load cards. Check your connection and try again.
                  </TableCell>
                </TableRow>
              ) : !paginated.length ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No profile cards yet.
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((card) => (
                  <CardRow
                    key={card.profile_card_id}
                    card={card}
                    onClick={() => setSelectedCard(card)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {(page > 1 || hasMore) && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="gap-1"
            >
              <IconChevronLeft className="size-3.5" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              className="gap-1"
            >
              Next
              <IconChevronRight className="size-3.5" />
            </Button>
          </div>
        )}
      </div>

      <CreateCardSheet open={createOpen} onClose={() => setCreateOpen(false)} />
      <CardDetailSheet
        card={selectedCard}
        onClose={() => setSelectedCard(null)}
      />
    </div>
  );
}
